<template>
  <!-- Auth Modal Backdrop & Container -->
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isOpen"
           class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
           @click="handleBackdropClick">
        <!-- Modal Card (Click-through prevention) -->
        <div class="w-full max-w-md"
             @click.stop>
          <Transition name="scale-up">
            <div v-if="isOpen"
                 class="bg-card rounded-lg shadow-2xl border border-border">
              <!-- Header -->
              <div class="flex items-center justify-between p-6 border-b border-border">
                <h2 class="text-2xl font-bold text-foreground">
                  {{ mode === 'login' ? 'Login' : 'Register' }}
                </h2>
                <button class="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-primary/10"
                        aria-label="Close modal"
                        @click="handleClose">
                  <font-awesome-icon :icon="['fal', 'times']"
                                     class="text-xl" />
                </button>
              </div>

              <!-- Form Content -->
              <div class="p-6 space-y-4">
                <!-- Login Form -->
                <form v-if="mode === 'login'"
                      class="space-y-4"
                      @submit.prevent="handleLogin">
                  <!-- Email/Username Input -->
                  <div>
                    <label for="login-email"
                           class="block text-sm font-medium text-foreground mb-2">
                      Email or Username
                    </label>
                    <input id="login-email"
                           v-model="loginForm.emailAddress"
                           type="text"
                           placeholder="Enter email or username"
                           class="w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                           :class="{ 'border-red-500': formErrors.emailAddress }">
                    <p v-if="formErrors.emailAddress"
                       class="text-red-500 text-sm mt-1">
                      {{ formErrors.emailAddress }}
                    </p>
                  </div>

                  <!-- Password Input -->
                  <div>
                    <label for="login-password"
                           class="block text-sm font-medium text-foreground mb-2">
                      Password
                    </label>
                    <input id="login-password"
                           v-model="loginForm.password"
                           type="password"
                           placeholder="Enter password"
                           class="w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                           :class="{ 'border-red-500': formErrors.password }">
                    <p v-if="formErrors.password"
                       class="text-red-500 text-sm mt-1">
                      {{ formErrors.password }}
                    </p>
                  </div>

                  <!-- Login Button -->
                  <button type="submit"
                          :disabled="isSubmitting"
                          class="w-full btn-primary rounded-lg px-4 py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    <span v-if="isSubmitting"
                          class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"></span>
                    {{ isSubmitting ? 'Logging in...' : 'Login' }}
                  </button>

                  <!-- Switch to Register -->
                  <p class="text-center text-sm text-muted-foreground">
                    Don't have an account?
                    <button type="button"
                            class="text-primary hover:underline font-semibold"
                            @click="mode = 'register'">
                      Register
                    </button>
                  </p>
                </form>

                <!-- Register Form -->
                <form v-if="mode === 'register'"
                      class="space-y-4"
                      @submit.prevent="handleRegister">

                  <!-- User Name -->
                  <div>
                    <label for="register-user-name"
                           class="block text-sm font-medium text-foreground mb-2">
                      User Name
                    </label>
                    <input id="register-user-name"
                           v-model="registerForm.username"
                           type="text"
                           placeholder="Enter user name"
                           class="w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                           :class="{ 'border-red-500': formErrors.username }">
                    <p v-if="formErrors.username"
                       class="text-red-500 text-sm mt-1">
                      {{ formErrors.username }}
                    </p>
                  </div>

                  <!-- First Name -->
                  <div>
                    <label for="register-first-name"
                           class="block text-sm font-medium text-foreground mb-2">
                      First Name
                    </label>
                    <input id="register-first-name"
                           v-model="registerForm.firstName"
                           type="text"
                           placeholder="Enter first name"
                           class="w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                           :class="{ 'border-red-500': formErrors.firstName }">
                    <p v-if="formErrors.firstName"
                       class="text-red-500 text-sm mt-1">
                      {{ formErrors.firstName }}
                    </p>
                  </div>

                  <!-- Last Name -->
                  <div>
                    <label for="register-last-name"
                           class="block text-sm font-medium text-foreground mb-2">
                      Last Name
                    </label>
                    <input id="register-last-name"
                           v-model="registerForm.lastName"
                           type="text"
                           placeholder="Enter last name"
                           class="w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                           :class="{ 'border-red-500': formErrors.lastName }">
                    <p v-if="formErrors.lastName"
                       class="text-red-500 text-sm mt-1">
                      {{ formErrors.lastName }}
                    </p>
                  </div>

                  <!-- Email -->
                  <div>
                    <label for="register-email"
                           class="block text-sm font-medium text-foreground mb-2">
                      Email Address
                    </label>
                    <input id="register-email"
                           v-model="registerForm.emailAddress"
                           type="email"
                           placeholder="Enter email address"
                           class="w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                           :class="{ 'border-red-500': formErrors.emailAddress }">
                    <p v-if="formErrors.emailAddress"
                       class="text-red-500 text-sm mt-1">
                      {{ formErrors.emailAddress }}
                    </p>
                  </div>

                  <!-- Password -->
                  <div>
                    <label for="register-password"
                           class="block text-sm font-medium text-foreground mb-2">
                      Password
                      <span class="text-xs text-muted-foreground">(min 8 chars, uppercase, lowercase, number)</span>
                    </label>
                    <input id="register-password"
                           v-model="registerForm.password"
                           type="password"
                           placeholder="Enter password"
                           class="w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                           :class="{ 'border-red-500': formErrors.password }">
                    <p v-if="formErrors.password"
                       class="text-red-500 text-sm mt-1">
                      {{ formErrors.password }}
                    </p>
                  </div>

                  <!-- Confirm Password -->
                  <div>
                    <label for="register-confirm-password"
                           class="block text-sm font-medium text-foreground mb-2">
                      Confirm Password
                    </label>
                    <input id="register-confirm-password"
                           v-model="registerForm.confirmPassword"
                           type="password"
                           placeholder="Confirm password"
                           class="w-full px-4 py-2 rounded-lg border border-input bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                           :class="{ 'border-red-500': formErrors.confirmPassword }">
                    <p v-if="formErrors.confirmPassword"
                       class="text-red-500 text-sm mt-1">
                      {{ formErrors.confirmPassword }}
                    </p>
                  </div>

                  <!-- Register Button -->
                  <button type="submit"
                          :disabled="isSubmitting"
                          class="w-full btn-primary rounded-lg px-4 py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    <span v-if="isSubmitting"
                          class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"></span>
                    {{ isSubmitting ? 'Creating account...' : 'Register' }}
                  </button>

                  <!-- Switch to Login -->
                  <p class="text-center text-sm text-muted-foreground">
                    Already have an account?
                    <button type="button"
                            class="text-primary hover:underline font-semibold"
                            @click="mode = 'login'">
                      Login
                    </button>
                  </p>
                </form>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useCartStore } from  '@/stores/cartStore';
import { useCheckoutStore} from '@/stores/checkoutStore';
import { useToast } from '@/composables/useToast';
import type { IUserRequest } from '@/data/_autogen/IValveVista';
import {useRouter} from 'vue-router';

interface Props {
  isOpen: boolean
}

defineProps<Props>();

const emit = defineEmits<{
  close: []
  loginSuccess: []
  registerSuccess: []
}>();

const authStore = useAuthStore();
const cartStore =  useCartStore();
const checkoutStore = useCheckoutStore();

const router = useRouter();

const { success, error: showError } = useToast();

const mode = ref<'login' | 'register'>('login');
const isSubmitting = ref(false);

// Login form state
const loginForm = reactive({
  emailAddress: '',
  password: '',
});

// Register form state
const registerForm = reactive({
  firstName: '',
  lastName: '',
  username: '',
  emailAddress: '',
  password: '',
  confirmPassword: '',
});

// Form errors
const formErrors = reactive({
  username: '',
  emailAddress: '',
  password: '',
  firstName: '',
  lastName: '',
  confirmPassword: '',
});

/**
 * Validate login form
 */
const validateLoginForm = (): boolean => {
  formErrors.emailAddress = '';
  formErrors.password = '';

  let isValid = true;

  if (!loginForm.emailAddress) {
    formErrors.emailAddress = 'Email or username required';
    isValid = false;
  }

  if (!loginForm.password) {
    formErrors.password = 'Password required';
    isValid = false;
  }

  return isValid;
};

/**
 * Validate register form
 */
const validateRegisterForm = (): boolean => {
  formErrors.firstName = '';
  formErrors.lastName = '';
  formErrors.username = '';
  formErrors.emailAddress = '';
  formErrors.password = '';
  formErrors.confirmPassword = '';

  let isValid = true;

  if (!registerForm.firstName) {
    formErrors.firstName = 'First name required';
    isValid = false;
  }

  if (!registerForm.lastName) {
    formErrors.lastName = 'Last name required';
    isValid = false;
  }

  if(!registerForm.username) {
    formErrors.username = 'Username required';
    isValid = false;
  }

  if (!registerForm.emailAddress) {
    formErrors.emailAddress = 'Email required';
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerForm.emailAddress)) {
    formErrors.emailAddress = 'Invalid email format';
    isValid = false;
  }

  const strongPasswordRegex = new RegExp(
    '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+={}\\[\\]:;"\'<>,.?/|\\~-])[A-Za-z\\d!@#$%^&*()_+={}\\[\\]:;"\'<>,.?/|\\~-]{8,}$'
  );

  if (!registerForm.password) {
    formErrors.password = 'Password required';
    isValid = false;
  } else if (
    registerForm.password.length < 8 || !strongPasswordRegex.test(registerForm.password)
    /*|| !/[A-Z]/.test(registerForm.password) ||
    !/[a-z]/.test(registerForm.password) ||
    !/[0-9]/.test(registerForm.password)*/
  ) {
    formErrors.password =
      'Password must be at least 8 characters with uppercase, lowercase, number and special character';
    isValid = false;
  }

  if (registerForm.password !== registerForm.confirmPassword) {
    formErrors.confirmPassword = 'Passwords do not match';
    isValid = false;
  }

  return isValid;
};

/**
 * Handle login form submission
 */
const handleLogin = async () => {
  if (!validateLoginForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const userRequest: IUserRequest = {
      emailAddress: loginForm.emailAddress.includes('@')? loginForm.emailAddress : undefined,
      userName: loginForm.emailAddress, // Can be either email or username
      password: loginForm.password,
    };

    const isGuest = authStore.isGuest;
    await authStore.login(userRequest);

    if(isGuest)
    {
      if(checkoutStore.checkoutSession)
        checkoutStore.resetCheckout();
      if(cartStore.cartId != null)
        cartStore.resetCart();
    }

    success('Login successful!');
    handleClose();
    emit('loginSuccess');
    await router.push('/');
  } catch (err: any) {
    const errorMessage =
      err.response?.data?.error || err.message || 'Login failed. Please try again.';
    showError(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

/**
 * Handle register form submission
 */
const handleRegister = async () => {
  if (!validateRegisterForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const userRequest: IUserRequest = {
      userName: registerForm.username.trim(),
      firstName: registerForm.firstName,
      lastName: registerForm.lastName,
      emailAddress: registerForm.emailAddress,
      password: registerForm.password.trim(),
    };

    const isGuest = authStore.isGuest;
    await authStore.register(userRequest);

    if(isGuest)
    {
      if(checkoutStore.checkoutSession)
        checkoutStore.resetCheckout();
      if(cartStore.cartId != null)
        cartStore.resetCart();
    }

    success('Registration successful!');
    handleClose();
    emit('registerSuccess');
    await router.push('/');
  } catch (err: any) {
    const errorMessage =
      err.response?.data?.error || err.message || 'Registration failed. Please try again.';
    showError(errorMessage);
  } finally {
    isSubmitting.value = false;

  }
};

const handleClose = () => {
  // Reset form
  loginForm.emailAddress = '';
  loginForm.password = '';
  registerForm.firstName = '';
  registerForm.lastName = '';
  registerForm.username = '';
  registerForm.emailAddress = '';
  registerForm.password = '';
  registerForm.confirmPassword = '';
  mode.value = 'login';

  emit('close');

};

const handleBackdropClick = () => {
  handleClose();
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-up-enter-active,
.scale-up-leave-active {
  transition: transform 0.3s ease;
}

.scale-up-enter-from,
.scale-up-leave-to {
  transform: scale(0.95);
}
</style>
